import { Controller } from "@hotwired/stimulus";
import Cropper from "cropperjs";

// Connects to data-controller="event-image-upload"
export default class extends Controller {
  static targets = [
    "uploadField",
    "uploadWrapper",
    "actionsWrapper",
    "imagePreview",
    "modal",
    "imageUploadContainer",
    "removeImgField",
  ];

  connect() {
    this.cropper = null;
    this.allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/jpg",
      "image/bmp",
      "image/svg+xml",
    ];

    this.boundDragOver = this.handleDragOver.bind(this);
    this.boundDragLeave = this.handleDragLeave.bind(this);
    this.boundDrop = this.handleDrop.bind(this);
    // Re-fit the cropper canvas to the (fluid) container on viewport changes so
    // the crop area always matches the current modal width on mobile/rotation.
    this.boundResizeCropper = this.resizeCropper.bind(this);

    const container = this.imageUploadContainerTarget;
    container.addEventListener("dragover", this.boundDragOver, true);
    container.addEventListener("dragleave", this.boundDragLeave, true);
    container.addEventListener("drop", this.boundDrop, true);

    window.addEventListener("resize", this.boundResizeCropper);
    window.addEventListener("orientationchange", this.boundResizeCropper);
  }

  disconnect() {
    const container = this.imageUploadContainerTarget;
    container.removeEventListener("dragover", this.boundDragOver, true);
    container.removeEventListener("dragleave", this.boundDragLeave, true);
    container.removeEventListener("drop", this.boundDrop, true);

    window.removeEventListener("resize", this.boundResizeCropper);
    window.removeEventListener("orientationchange", this.boundResizeCropper);
  }

  resizeCropper() {
    if (this.cropper) this.cropper.resize();
  }

  triggerImageInput(event) {
    this.uploadFieldTarget.click();
  }

  handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    this.imageUploadContainerTarget.classList.add("drag-over");
  }

  handleDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();

    const container = this.imageUploadContainerTarget;
    if (event.relatedTarget && container.contains(event.relatedTarget)) return;

    container.classList.remove("drag-over");
  }

  handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    this.imageUploadContainerTarget.classList.remove("drag-over");

    if (this.modalTarget.style.display === "block") return;

    const file = event.dataTransfer.files[0];
    if (!file) return;

    this.processFile(file);
  }

  previewImage(event) {
    this.processFile(event.target.files[0]);
  }

  processFile(file) {
    if (!file) return;

    if (!this.allowedTypes.includes(file.type)) {
      this.uploadFieldTarget.value = "";
      alert(this.getErrorMessage(file.type));
      return;
    }

    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    this.uploadFieldTarget.files = dataTransfer.files;

    // Choosing a new image cancels any pending removal.
    if (this.hasRemoveImgFieldTarget) this.removeImgFieldTarget.value = "false";

    const reader = new FileReader();
    reader.onload = (e) => {
      this.imagePreviewTarget.src = e.target.result;
      this.openModal();
      // Banner crop UX: the user REPOSITIONS the photo behind a fixed 2:1 frame.
      // `dragMode: "move"` makes a one-finger / mouse drag PAN the image (not
      // just resize the crop box), which is the interaction that was missing
      // before (the default dragMode:"crop" locked them to a single framing).
      // `viewMode: 1` keeps the CROP BOX inside the canvas (image) so it can
      // never spill past the modal edges — viewMode:0 let the crop box leave the
      // canvas, which is the overflow bug we're fixing. Repositioning still
      // works because with dragMode:"move" the user PANS THE IMAGE behind the
      // contained crop box (viewMode:1 only restricts the crop box, not the
      // image's freedom to move/zoom). Image is movable + zoomable (wheel on
      // desktop, pinch on touch) so they can frame the subject, and the crop box
      // itself is also movable/resizable but stays within the image. aspectRatio:2
      // keeps the output 2:1 regardless of how they pan/zoom.
      this.cropper = new Cropper(this.imagePreviewTarget, {
        aspectRatio: 2,
        viewMode: 1,
        dragMode: "move",
        autoCropArea: 0.8,
        movable: true,
        zoomable: true,
        zoomOnWheel: true,
        zoomOnTouch: true,
        cropBoxMovable: true,
        cropBoxResizable: true,
        minCropBoxWidth: 100,
        minCropBoxHeight: 50,
        responsive: true,
      });
    };
    reader.readAsDataURL(file);
  }

  getErrorMessage(fileType) {
    return `Unsupported file type: ${fileType}. Please upload a valid image file (${this.allowedTypes.join(
      ", "
    )}).`;
  }

  removeImage() {
    this.imagePreviewTarget.src = "";
    this.imageUploadContainerTarget.style.backgroundImage = "";
    this.uploadWrapperTarget.classList.remove("d-none");
    this.actionsWrapperTarget.classList.add("d-none");

    this.uploadFieldTarget.value = "";
    // Flag the existing attachment for removal on save (Eventable#update purges).
    if (this.hasRemoveImgFieldTarget) this.removeImgFieldTarget.value = "true";
  }

  openModal() {
    this.modalTarget.style.display = "block";
    // The cropper may have been built while the modal was display:none (zero
    // size). Now that it's visible, re-fit the canvas to the container width.
    this.resizeCropper();
  }

  closeModal() {
    this.modalTarget.style.display = "none";
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
  }

  // Visible zoom controls. Cropper.js v1.6.2 `.zoom(ratio)` zooms relative to the
  // current scale; +0.1 / -0.1 are gentle steps. Guarded in case the cropper has
  // not been built yet (modal closed). Wheel/pinch zoom remain unaffected.
  zoomIn(e) {
    e?.preventDefault();
    this.cropper?.zoom(0.1);
  }

  zoomOut(e) {
    e?.preventDefault();
    this.cropper?.zoom(-0.1);
  }

  cropImage() {
    if (this.cropper) {
      const canvas = this.cropper.getCroppedCanvas();

      canvas.toBlob((blob) => {
        const croppedFile = new File([blob], "cropped-image.png", {
          type: "image/png",
        });

        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(croppedFile);

        this.uploadFieldTarget.files = dataTransfer.files;

        const croppedImageURL = URL.createObjectURL(blob);
        this.imageUploadContainerTarget.style.backgroundImage = `url(${croppedImageURL})`;

        this.closeModal();
        this.uploadWrapperTarget.classList.add("d-none");
        this.actionsWrapperTarget.classList.remove("d-none");
      }, "image/png");
    }
  }
}
